import { Button, Result, Spin } from 'antd';
import { CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '@/hooks/usePlan';
import { useRbac } from '@/hooks/useRbac';
import { ROUTES } from '@/constants/routes';
import type { PlanTier } from '@/redux/slices/planSlice';
import { PLAN_LABELS } from '@/redux/slices/planSlice';

type Mode = 'hide' | 'disable' | 'upgrade';

interface FeatureGateProps {
  /** Feature key checked via usePlan().can(feature) */
  feature?: string;
  /** Alternative: require minimum plan tier */
  minTier?: PlanTier;
  /** RBAC permission key checked via useRbac().hasPermission(key) */
  permission?: string;
  /** What to do when gated. Default 'upgrade' shows prompt page. */
  mode?: Mode;
  /** Custom fallback overrides mode */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Gate content by plan feature/tier hoặc RBAC permission.
 * - mode='hide':    render nothing
 * - mode='disable': render children inside a disabled-looking wrapper
 * - mode='upgrade': render upgrade CTA (default)
 */
export function FeatureGate({ feature, minTier, permission, mode = 'upgrade', fallback, children }: FeatureGateProps) {
  const { can, atLeast, isLoaded } = usePlan();
  const { hasPermission, isLoading: isRbacLoading } = useRbac();
  const navigate = useNavigate();

  const requiresPlan = Boolean(feature || minTier);
  const isCheckingAccess = (requiresPlan && !isLoaded) || (Boolean(permission) && isRbacLoading);
  if (isCheckingAccess) {
    if (mode === 'upgrade') {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }
    return null;
  }

  const planOk = (feature ? can(feature) : true) && (minTier ? atLeast(minTier) : true);
  const rbacOk = permission ? hasPermission(permission) : true;
  const allowed = planOk && rbacOk;
  if (allowed) return <>{children}</>;

  if (fallback) return <>{fallback}</>;

  if (mode === 'hide') return null;

  if (mode === 'disable') {
    return (
      <div style={{ opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }} aria-disabled>
        {children}
      </div>
    );
  }

  const requiredLabel = minTier ? PLAN_LABELS[minTier] : 'gói cao hơn';
  return (
    <Result
      icon={<CrownOutlined style={{ color: '#a4193d' }} />}
      title="Tính năng này yêu cầu nâng cấp gói"
      subTitle={
        feature
          ? `Tính năng "${feature}" chưa có trong gói hiện tại. Nâng cấp lên ${requiredLabel} để sử dụng.`
          : `Nâng cấp lên ${requiredLabel} để truy cập tính năng này.`
      }
      extra={
        <Button type="primary" onClick={() => navigate(ROUTES.settings.billing)}>
          Nâng cấp ngay
        </Button>
      }
    />
  );
}
